import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchOrders as fetchOrdersService,
  fetchOrderSummary as fetchOrderSummaryService,
  fetchOrderDetail as fetchOrderDetailService,
  fetchCancelInfo as fetchCancelInfoService,
  cancelOrder as cancelOrderService,
  reorder as reorderService,
  fetchReturnInfo as fetchReturnInfoService,
  returnPreview as returnPreviewService,
  returnOrder as returnOrderService,
} from "./orders.service";
import {
  OrdersResponse,
  OrderDetailResponse,
  OrderSummaryResponse,
  CancelInfoResponse,
  CancelOrderResponse,
  ReorderResponse,
  ReturnInfoResponse,
  ReturnPreviewResponse,
  ReturnOrderResponse,
  GetOrdersRequest,
  GetOrderSummaryRequest,
  CancelOrderRequest,
  ReorderRequest,
  ReturnPreviewRequest,
  ReturnOrderRequest,
} from "./orders.types";
import { handleApiError } from "@/app/utils/handleApiError";

export const getOrders = createAsyncThunk<
  OrdersResponse,
  GetOrdersRequest | undefined,
  { rejectValue: string }
>("orders/getOrders", async (params, { rejectWithValue }) => {
  try {
    const response = await fetchOrdersService(params);
    return response;
  } catch (error) {
    return rejectWithValue(handleApiError(error));
  }
});

export const getOrderSummary = createAsyncThunk<
  OrderSummaryResponse,
  GetOrderSummaryRequest | undefined,
  { rejectValue: string }
>("orders/getOrderSummary", async (params, { rejectWithValue }) => {
  try {
    const response = await fetchOrderSummaryService(params);
    return response;
  } catch (error) {
    return rejectWithValue(handleApiError(error));
  }
});

export const getOrderDetail = createAsyncThunk<
  OrderDetailResponse,
  string,
  { rejectValue: string }
>("orders/getOrderDetail", async (orderNumber, { rejectWithValue }) => {
  try {
    const response = await fetchOrderDetailService(orderNumber);
    return response;
  } catch (error) {
    return rejectWithValue(handleApiError(error));
  }
});

export const getCancelInfo = createAsyncThunk<
  CancelInfoResponse,
  string,
  { rejectValue: string }
>("orders/getCancelInfo", async (orderNumber, { rejectWithValue }) => {
  try {
    const response = await fetchCancelInfoService(orderNumber);
    return response;
  } catch (error) {
    return rejectWithValue(handleApiError(error));
  }
});

export const cancelOrderThunk = createAsyncThunk<
  CancelOrderResponse,
  { orderNumber: string; data?: CancelOrderRequest },
  { rejectValue: string }
>("orders/cancelOrder", async ({ orderNumber, data }, { rejectWithValue }) => {
  try {
    const response = await cancelOrderService(orderNumber, data);
    return response;
  } catch (error) {
    return rejectWithValue(handleApiError(error));
  }
});

export const reorderThunk = createAsyncThunk<
  ReorderResponse,
  { orderNumber: string; params?: ReorderRequest },
  { rejectValue: string }
>("orders/reorder", async ({ orderNumber, params }, { rejectWithValue }) => {
  try {
    const response = await reorderService(orderNumber, params);
    return response;
  } catch (error) {
    return rejectWithValue(handleApiError(error));
  }
});

export const getReturnInfo = createAsyncThunk<
  ReturnInfoResponse,
  string,
  { rejectValue: string }
>("orders/getReturnInfo", async (orderNumber, { rejectWithValue }) => {
  try {
    const response = await fetchReturnInfoService(orderNumber);
    return response;
  } catch (error) {
    return rejectWithValue(handleApiError(error));
  }
});

export const getReturnPreview = createAsyncThunk<
  ReturnPreviewResponse,
  { orderNumber: string; data: ReturnPreviewRequest },
  { rejectValue: string }
>("orders/getReturnPreview", async ({ orderNumber, data }, { rejectWithValue }) => {
  try {
    const response = await returnPreviewService(orderNumber, data);
    return response;
  } catch (error) {
    return rejectWithValue(handleApiError(error));
  }
});

export const submitReturn = createAsyncThunk<
  ReturnOrderResponse,
  { orderNumber: string; data: ReturnOrderRequest },
  { rejectValue: string }
>("orders/submitReturn", async ({ orderNumber, data }, { rejectWithValue }) => {
  try {
    const response = await returnOrderService(orderNumber, data);
    return response;
  } catch (error) {
    return rejectWithValue(handleApiError(error));
  }
});